import React, { useEffect, useRef, useState } from 'react';
import { Button, Card, Col, Form, Input, Modal, Popconfirm, Row, Select, Space, Statistic, Switch, Table, Tag, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { CustomerServiceOutlined, DeleteOutlined, EditOutlined, PlayCircleOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { get, post, put, del } from '../../api/request';
import { API_ENDPOINTS } from '../../api/config';
import { attachHlsSource } from '../../components/HlsMedia';
import './contentOps.css';

const RES = 'voices';

interface Voice {
  id: number;
  name: string;
  gender: 'female' | 'male' | 'neutral';
  styleTags: string[];
  provider: string;
  voiceType: string;
  scene: string[];
  isDefault: boolean;
  status: number;
  sampleText?: string;
  sampleUrl?: string;
}

const genderMeta = {
  female: { text: '女声', color: 'magenta' },
  male: { text: '男声', color: 'blue' },
  neutral: { text: '中性', color: 'cyan' },
} as const;

const providerOptions = [
  { label: 'MiniMax', value: 'MiniMax' },
  { label: 'QuickRouter', value: 'QuickRouter' },
];

const styleOptions = ['柔声', '轻故事', '低频', '短回应', '治愈', '磁性', '清亮', '中性平稳'].map((v) => ({ label: v, value: v }));
const sceneOptions = ['哄睡', '唤醒', '陪伴', '冥想', '播报'].map((v) => ({ label: v, value: v }));

const legacyVoiceMap: Record<string, Partial<Voice>> = {
  zh_female_zhixin: { name: '栀晚', provider: 'QuickRouter', voiceType: 'female-shaonv', styleTags: ['柔声', '轻故事'], scene: ['哄睡', '陪伴'], isDefault: true, status: 1 },
  zh_male_yejun: { name: '澈川', provider: 'QuickRouter', voiceType: 'male-qn-qingse', styleTags: ['低频', '短回应'], scene: ['陪伴', '唤醒'], status: 1 },
  minimax_neutral_01: { name: '小宇宙', provider: 'QuickRouter', voiceType: 'audiobook_female_1', styleTags: ['中性平稳', '清亮'], scene: ['唤醒', '冥想'], status: 1 },
};

const VoiceManagement: React.FC = () => {
  const [form] = Form.useForm();
  const [items, setItems] = useState<Voice[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const audioCleanupRef = useRef<(() => void) | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Voice | null>(null);
  const [genderFilter, setGenderFilter] = useState<string>();
  const [previewingId, setPreviewingId] = useState<number | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      let data = await get<Voice[]>(API_ENDPOINTS.operations.base(RES));
      const legacyItems = (data || []).filter((item) => legacyVoiceMap[item.voiceType]);
      if (legacyItems.length) {
        await Promise.all(legacyItems.map((item) => put(API_ENDPOINTS.operations.detail(RES, item.id), { ...item, ...legacyVoiceMap[item.voiceType] })));
        data = await get<Voice[]>(API_ENDPOINTS.operations.base(RES));
      }
      setItems(data || []);
    } catch (e) {
      message.error('加载失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSave = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      if (editing) {
        await put(API_ENDPOINTS.operations.detail(RES, editing.id), values);
      } else {
        await post(API_ENDPOINTS.operations.base(RES), values);
      }
      message.success('已保存');
      setModalOpen(false);
      await load();
    } catch (e) {
      message.error('保存失败');
    } finally {
      setSaving(false);
    }
  };

  const deleteItem = async (record: Voice) => {
    try {
      await del(API_ENDPOINTS.operations.detail(RES, record.id));
      message.success('已删除');
      await load();
    } catch (e) {
      message.error('删除失败');
    }
  };

  const toggleStatus = async (record: Voice, checked: boolean) => {
    try {
      await put(API_ENDPOINTS.operations.detail(RES, record.id), { status: checked ? 1 : 0 });
      await load();
      message.success(checked ? '已启用，用户端可选择' : '已停用，用户端不再展示');
    } catch (e) {
      message.error('状态更新失败');
      await load();
    }
  };

  const openCreate = () => {
    setEditing(null);
    form.setFieldsValue({ name: '', gender: 'female', styleTags: [], provider: 'QuickRouter', voiceType: '', scene: [], sampleText: '', sampleUrl: '', isDefault: false, status: 1 });
    setModalOpen(true);
  };
  const openEdit = (record: Voice) => {
    setEditing(record);
    form.setFieldsValue(record);
    setModalOpen(true);
  };

  const filtered = items.filter((i) => !genderFilter || i.gender === genderFilter);
  const total = items.length;
  const enabled = items.filter((i) => i.status === 1).length;
  const female = items.filter((i) => i.gender === 'female').length;
  const male = items.filter((i) => i.gender === 'male').length;

  const previewVoice = async (record: Voice) => {
    if (!record.voiceType) {
      message.warning('请先配置 voice_type');
      return;
    }
    setPreviewingId(record.id);
    try {
      const sampleText = record.sampleText || `${record.name}正在为你试听。夜色很安静,请跟着声音慢慢放松下来。`;
      let src = record.sampleUrl || '';
      if (!src) {
        const result = await post<{
          audio_url?: string;
          audio_base64?: string;
          audio_format?: string;
        }>(API_ENDPOINTS.ai_management.testTts, {
          provider: 'quickrouter',
          voice: record.voiceType,
          text: sampleText,
          speed: 1,
          pitch: 0,
          volume: 1,
        });
        src = result.audio_url || (result.audio_base64 ? `data:audio/${result.audio_format || 'mp3'};base64,${result.audio_base64}` : '');
      }
      if (!src) {
        throw new Error('没有拿到试听音频');
      }
      if (!audioRef.current) {
        audioRef.current = new Audio();
      }
      audioRef.current.pause();
      audioCleanupRef.current?.();
      audioCleanupRef.current = attachHlsSource(audioRef.current, src);
      audioRef.current.onended = () => setPreviewingId(null);
      audioRef.current.onerror = () => {
        setPreviewingId(null);
        message.error('试听播放失败');
      };
      await audioRef.current.play();
      message.success('开始试听');
    } catch (e) {
      setPreviewingId(null);
      message.error(e instanceof Error ? e.message : '试听失败');
    }
  };

  const columns: ColumnsType<Voice> = [
    {
      title: '音色',
      dataIndex: 'name',
      render: (_, record) => (
        <div className="content-ops-title-cell">
          <div className="content-ops-cover" style={{ fontSize: 18 }}><CustomerServiceOutlined /></div>
          <div>
            <strong>{record.name} {record.isDefault && <Tag color="gold">默认</Tag>}</strong>
            <span>{record.styleTags.join(' · ')}</span>
          </div>
        </div>
      ),
    },
    { title: '性别', dataIndex: 'gender', width: 80, render: (v: Voice['gender']) => <Tag color={genderMeta[v].color}>{genderMeta[v].text}</Tag> },
    { title: '供应商', dataIndex: 'provider', width: 130, render: (v) => <Tag>{v}</Tag> },
    { title: 'voice_type', dataIndex: 'voiceType', width: 180, render: (v) => <code>{v}</code> },
    {
      title: '适用场景', dataIndex: 'scene', width: 170,
      render: (scene: string[]) => <>{scene.map((s) => <Tag key={s} color="geekblue">{s}</Tag>)}</>,
    },
    {
      title: '试听',
      width: 100,
      render: (_, record) => (
        <Button
          size="small"
          type="link"
          icon={<PlayCircleOutlined />}
          disabled={!record.voiceType}
          loading={previewingId === record.id}
          className={previewingId === record.id ? 'voice-preview-playing' : ''}
          onClick={() => previewVoice(record)}
        >
          试听
        </Button>
      ),
    },
    { title: '启用', dataIndex: 'status', width: 80, render: (v, record) => <Switch checked={v === 1} onChange={(checked) => toggleStatus(record, checked)} /> },
    {
      title: '操作', width: 130, fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)}>编辑</Button>
          <Popconfirm title="确认删除该音色？" onConfirm={() => deleteItem(record)}><Button size="small" danger icon={<DeleteOutlined />} /></Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="content-ops-page">
      <div className="content-ops-header">
        <div>
          <h1 className="page-title">声音音色管理</h1>
          <p className="content-ops-subtitle">维护用户端「陪伴声线」可选的音色库，配置性别、风格标签、TTS 供应商与 voice_type、适用场景与默认音色，是声音体验的核心配置。</p>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={load}>刷新</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>新增音色</Button>
        </Space>
      </div>

      <Row gutter={16} className="content-ops-stats">
        <Col span={6}><Card className="content-ops-stat-card"><Statistic title="音色总数" value={total} prefix={<CustomerServiceOutlined />} /></Card></Col>
        <Col span={6}><Card className="content-ops-stat-card"><Statistic title="已启用" value={enabled} valueStyle={{ color: '#52c41a' }} /></Card></Col>
        <Col span={6}><Card className="content-ops-stat-card"><Statistic title="女声音色" value={female} valueStyle={{ color: '#eb2f96' }} /></Card></Col>
        <Col span={6}><Card className="content-ops-stat-card"><Statistic title="男声音色" value={male} valueStyle={{ color: '#1677ff' }} /></Card></Col>
      </Row>

      <Card bordered={false}>
        <div className="content-ops-toolbar">
          <div className="toolbar-filters">
            <Select placeholder="性别筛选" allowClear style={{ width: 140 }} options={[{ label: '女声', value: 'female' }, { label: '男声', value: 'male' }, { label: '中性', value: 'neutral' }]} onChange={setGenderFilter} />
          </div>
        </div>
        <Table rowKey="id" columns={columns} dataSource={filtered} loading={loading} pagination={{ pageSize: 8 }} scroll={{ x: 1150 }} />
      </Card>

      <Modal title={editing ? '编辑音色' : '新增音色'} open={modalOpen} onCancel={() => setModalOpen(false)} onOk={handleSave} confirmLoading={saving} width={680} destroyOnClose>
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}><Form.Item label="音色名称" name="name" rules={[{ required: true }]}><Input placeholder="如：栀晚" /></Form.Item></Col>
            <Col span={12}><Form.Item label="性别" name="gender"><Select options={[{ label: '女声', value: 'female' }, { label: '男声', value: 'male' }, { label: '中性', value: 'neutral' }]} /></Form.Item></Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}><Form.Item label="供应商" name="provider"><Select options={providerOptions} /></Form.Item></Col>
            <Col span={12}><Form.Item label="voice_type" name="voiceType"><Input placeholder="供应商音色编码" /></Form.Item></Col>
          </Row>
          <Form.Item label="风格标签" name="styleTags"><Select mode="multiple" options={styleOptions} /></Form.Item>
          <Form.Item label="适用场景" name="scene"><Select mode="multiple" options={sceneOptions} /></Form.Item>
          <Row gutter={16}>
            <Col span={12}><Form.Item label="试听文案" name="sampleText"><Input placeholder="可为空，默认随机短句" /></Form.Item></Col>
            <Col span={12}><Form.Item label="试听样本 URL" name="sampleUrl"><Input placeholder="可为空，不填则实时 AI 合成" /></Form.Item></Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}><Form.Item label="设为默认音色" name="isDefault" valuePropName="checked"><Switch checkedChildren="默认" unCheckedChildren="普通" /></Form.Item></Col>
            <Col span={12}><Form.Item label="状态" name="status"><Select options={[{ label: '启用', value: 1 }, { label: '停用', value: 0 }]} /></Form.Item></Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default VoiceManagement;
